import React, { useEffect, useState } from 'react';
import './App.css';
import { Routes, Route } from "react-router-dom";
import Home from './views/home';
import TrackOrder from './views/trackOrder';
import NewRequest from './views/newRequest';
import ValidateAccount from './views/validateAccount/index';
import UnauthorizedUser from './views/unauthorizedUser';
import HeaderComponent from './components/HeaderComponent';
import { getSessionData, updateManifestFile } from './utils/utils';
import PushNotificationTest from './PushNotificationTest';
import { GoogleMapsProvider } from './utils/GoogleMapsProvider';
function App() {
  const [isAppReady, setIsAppReady] = useState(false);
  const [appError, setAppError] = useState(null);
  
  const accountId = getSessionData('userID');
  const uniqueCode = getSessionData('token');

  // Enhanced app initialization with proper error handling
  useEffect(() => {
    const initializeApp = async () => {
      try {
        console.log('Initializing All City Tow app...');
        
        // Update manifest if we have account details
        if (accountId && uniqueCode) {
          try {
            await updateManifestFile(accountId, uniqueCode);
            console.log('Manifest updated successfully');
          } catch (manifestError) {
            console.error('Error updating manifest:', manifestError);
            // Don't fail app initialization for manifest errors
          }
        }
        
        // Check if we're in a supported environment
        const platform = detectPlatform();
        console.log('Platform detected:', platform);
        
        // For iOS, do additional checks
        if (platform.isIOS) {
          await handleIOSInitialization(platform);
        }
        
        setIsAppReady(true);
        console.log('App initialization complete');
        
      } catch (error) {
        console.error('App initialization failed:', error);
        setAppError(error.message);
        setIsAppReady(true); // Still allow app to load
      }
    };

    initializeApp();
  }, [accountId, uniqueCode]);

  // Platform detection helper
  const detectPlatform = () => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isAndroid = /android/i.test(ua);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
    const isSimulator = ua.includes('iPhone Simulator') || ua.includes('iPad Simulator') ||
                        (isIOS && (window.location.hostname === 'localhost' || window.location.port === '3000'));
    
    let iOSVersion = null;
    if (isIOS) {
      const match = ua.match(/OS (\d+)_?(\d+)?/);
      if (match) {
        iOSVersion = {
          major: parseInt(match[1], 10),
          minor: match[2] ? parseInt(match[2], 10) : 0
        };
      }
    }
    
    return {
      isIOS,
      isAndroid,
      isMobile: isIOS || isAndroid,
      isStandalone,
      isSimulator,
      iOSVersion,
      userAgent: ua
    };
  };

  // iOS-specific initialization
  const handleIOSInitialization = async (platform) => {
    try {
      console.log('Handling iOS-specific initialization');
      
      // Check iOS version compatibility
      if (platform.iOSVersion) {
        const { major, minor } = platform.iOSVersion;
        console.log(`iOS version: ${major}.${minor}`);
        
        if (major < 14) {
          console.warn('iOS version may not support all features');
        }
        
        // For iOS 16.4+, check web push availability
        if (major > 16 || (major === 16 && minor >= 4)) {
          console.log('iOS supports web push notifications');
          if (!platform.isStandalone) {
            console.log('Consider adding to home screen for full functionality');
          }
        } else {
          console.log('iOS version does not support web push notifications');
        }
      }
      
      // Handle iOS simulator
      if (platform.isSimulator) {
        console.log('iOS Simulator detected - some features may be limited');
        // Set global flag for other components
        window._isIOSSimulator = true;
      }
      
      // iOS-specific app settings
      if (platform.isStandalone) {
        console.log('Running in standalone mode on iOS');
        // Prevent zoom on double tap
        document.addEventListener('touchstart', function(event) {
          if (event.touches.length > 1) {
            event.preventDefault();
          }
        }, { passive: false });
        
        // Handle viewport changes for iOS
        handleIOSViewport();
      }
      
    } catch (iosError) {
      console.error('Error in iOS initialization:', iosError);
      // Don't throw - let app continue
    }
  };

  // Handle iOS viewport and orientation changes
  const handleIOSViewport = () => {
    const updateViewport = () => {
      // Handle iOS viewport height changes
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    
    updateViewport();
    
    // Update on resize and orientation change
    window.addEventListener('resize', updateViewport);
    window.addEventListener('orientationchange', () => {
      // Delay for iOS orientation change
      setTimeout(updateViewport, 100);
    });
    
    // Handle iOS safe area
    if (CSS.supports('padding-top: env(safe-area-inset-top)')) {
      document.documentElement.style.setProperty('--safe-area-inset-top', 'env(safe-area-inset-top)');
      document.documentElement.style.setProperty('--safe-area-inset-bottom', 'env(safe-area-inset-bottom)');
    }
  };

  // Error boundary component
  const ErrorDisplay = ({ error }) => (
    <div style={{
      padding: '20px',
      margin: '20px',
      backgroundColor: '#ffebee',
      border: '1px solid #f44336',
      borderRadius: '4px',
      color: '#d32f2f'
    }}>
      <h3>App Initialization Warning</h3>
      <p>{error}</p>
      <p>The app will continue to work, but some features may be limited.</p>
    </div>
  );

  // Loading component
  const LoadingScreen = () => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      backgroundColor: '#f5f5f5'
    }}>
      <div style={{
        width: '50px',
        height: '50px',
        border: '3px solid #f3f3f3',
        borderTop: '3px solid #3498db',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }}></div>
      <p style={{ marginTop: '20px', color: '#666' }}>
        Initializing All City Tow...
      </p>
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );

  // Don't render the app until initialization is complete
  if (!isAppReady) {
    return <LoadingScreen />;
  }

  return (
    <div className="App">
      {/* Add CSS custom properties for iOS */}
      <style jsx global>{`
        :root {
          --vh: 1vh;
          --safe-area-inset-top: 0px;
          --safe-area-inset-bottom: 0px;
        }
        
        .App {
          min-height: 100vh;
          min-height: calc(var(--vh, 1vh) * 100);
          padding-top: var(--safe-area-inset-top);
          padding-bottom: var(--safe-area-inset-bottom);
        }
        
        /* iOS-specific fixes */
        @supports (-webkit-touch-callout: none) {
          /* iOS Safari specific styles */
          .App {
            -webkit-touch-callout: none;
            -webkit-user-select: none;
            -webkit-tap-highlight-color: transparent;
          }
        }
        
        /* Prevent zoom on iOS */
        input, select, textarea {
          font-size: 16px !important;
        }
        
        /* Fix iOS input focus zoom */
        @media screen and (max-width: 768px) {
          input[type="text"],
          input[type="email"],
          input[type="tel"],
          input[type="password"],
          textarea,
          select {
            font-size: 16px !important;
            transform-origin: left top;
            transform: scale(1);
          }
        }
      `}</style>

      <HeaderComponent />
      
      {/* Show error if there was an initialization problem */}
      {appError && <ErrorDisplay error={appError} />}
      
      {/* Push notification component - now properly integrated */}
      <PushNotificationTest />
      <GoogleMapsProvider>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="newrequest" element={<NewRequest />} />
          <Route path="trackOrder" element={<TrackOrder />} />
          <Route path="validateAcct/:accountId/:uniqueCode" element={<ValidateAccount />} />
       
      
        <Route path="unauthorizedUser" element={<UnauthorizedUser />} />
      </Routes>
      </GoogleMapsProvider>
    </div>
  );
}

export default App;